// Numbers for the Reports view: who's in the tree, how they identify, when and where they were
// born, how long they lived, how the family is put together, and what looks worth checking.
// Pure; tested in tests/stats.test.ts.

import { ageOf, ageText, countableDate, midAge, yearsBetween, type Age } from './countdown';
import { dateSortKey, dateYear, parseGedcomDate } from './dates';
import { componentSizes, descendantsOf, graphOf, parentsOf, type Graph } from './graph';
import { birthDate, deathDate, displayName, GENDER_LABELS, genderLabel, isDeceased, isLiving, lifeSpan, primaryName, termGender, type TermGender } from './people';
import { normalize } from './search';
import type { GenderIdentity, Id, ParentType, PartnerType, Person, SocialType, Tree } from './types';

export interface Count {
  key: string;
  label: string;
  count: number;
}

export type FactTopic = 'life' | 'birthday' | 'family' | 'partners' | 'identity' | 'names' | 'groups' | 'links';

export interface Fact {
  /** The whole fact as a sentence. */
  text: string;
  /** For showing it as a tile: a short heading, the headline number or name, and a line under it. */
  label?: string;
  value?: string;
  note?: string;
  topic?: FactTopic;
  /** People the fact is about, so the UI can link to them. */
  people?: Id[];
}

export interface TreeStats {
  people: number;
  living: number;
  deceased: number;
  /** Living status not recorded. */
  notRecorded: number;
  generations: number;
  partnerships: number;
  groups: number;
  genders: Count[];
  terms: Record<TermGender, number>;
  pronouns: Count[];
  birthDecades: Count[];
  /** Earliest and latest birth years recorded. */
  birthYears?: [number, number];
  /** January to December, for people whose birth date has a month. */
  birthMonths: Count[];
  livingAges: Count[];
  /** Ten-year bands of age at death. */
  deathAges: Count[];
  surnames: Count[];
  /** How many different surnames there are. */
  surnameCount: number;
  birthplaces: Count[];
  occupations: Count[];
  /** How many people have each basic detail recorded, out of everyone. */
  completeness: Count[];
  /** Of the people who have died, how many have a death date. */
  deathDates: { known: number; of: number };
  parentLinks: Count[];
  partnerKinds: Count[];
  socialKinds: Count[];
  facts: Fact[];
  /** Things in the records that look like mistakes. */
  checks: Fact[];
}

const PARENT_LABELS: Record<ParentType, string> = {
  biological: 'Biological', adoptive: 'Adoptive', step: 'Step', foster: 'Foster', guardian: 'Guardian',
  chosen: 'Chosen parent', surrogate: 'Surrogate', donor: 'Donor', unknown: 'Unknown',
};
const PARTNER_LABELS: Record<PartnerType, string> = {
  married: 'Married', partner: 'Partners', 'civil-union': 'Civil union', domestic: 'Domestic partnership', engaged: 'Engaged', dating: 'Dating', other: 'Other',
};
const SOCIAL_LABELS: Record<SocialType, string> = {
  friend: 'Friends', 'close-friend': 'Close friends', 'best-friend': 'Best friends',
  'family-friend': 'Family friends', mentor: 'Mentors', roommate: 'Roommates', colleague: 'Colleagues', neighbor: 'Neighbors', other: 'Other',
};
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Recorded as living, but born longer ago than anyone lives. */
const OLDEST_PLAUSIBLE = 110;

/** Tally values, most common first; keep `top` and fold the rest into "Other". */
export function tally(values: string[], top = Infinity, labels?: (k: string) => string): Count[] {
  const m = new Map<string, number>();
  for (const v of values) m.set(v, (m.get(v) ?? 0) + 1);
  const all = [...m].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([key, count]) => ({ key, label: labels ? labels(key) : key, count }));
  if (all.length <= top) return all;
  const rest = all.slice(top).reduce((n, c) => n + c.count, 0);
  return [...all.slice(0, top), { key: '__other', label: `Other (${all.length - top})`, count: rest }];
}

/**
 * Tally names and places the way people mean them: "SMITH", "Smith", and "Smith " are one
 * surname (keyed as search normalizes it, so search's key:=value finds the same people), shown
 * in its most used spelling, preferring one that isn't all capitals.
 */
export function tallyText(values: string[], top = Infinity): Count[] {
  const spellings = new Map<string, Map<string, number>>();
  const keys: string[] = [];
  for (const raw of values) {
    const v = raw.trim().replace(/\s+/g, ' ');
    const k = normalize(v);
    if (!k) continue;
    keys.push(k);
    const m = spellings.get(k) ?? new Map<string, number>();
    m.set(v, (m.get(v) ?? 0) + 1);
    spellings.set(k, m);
  }
  const shouty = (s: string) => s === s.toUpperCase() && s !== s.toLowerCase();
  const label = (k: string) => [...spellings.get(k)!].sort((a, b) => b[1] - a[1] || Number(shouty(a[0])) - Number(shouty(b[0])) || a[0].localeCompare(b[0]))[0][0];
  return tally(keys, top, label);
}

/** Ten-year bands up to one "100+" band for everyone older. */
function ageBands(ages: number[]): Count[] {
  const bands: Count[] = [];
  if (!ages.length) return bands;
  const max = Math.max(...ages);
  for (let lo = 0; lo <= Math.min(100, max); lo += 10)
    bands.push({ key: String(lo), label: lo >= 100 ? '100+' : `${lo}–${lo + 9}`, count: ages.filter((a) => (lo >= 100 ? a >= 100 : a >= lo && a < lo + 10)).length });
  return bands;
}

/** The longest chain of parent → child links among `ids` (cycle-safe), with the people at each end. */
function longestLine(g: Graph, ids: Iterable<Id>, include: (id: Id) => boolean = () => true): { n: number; top: Id; bottom: Id } | undefined {
  const memo = new Map<Id, { n: number; top: Id }>();
  const visiting = new Set<Id>();
  const up = (id: Id): { n: number; top: Id } => {
    const known = memo.get(id);
    if (known) return known;
    if (visiting.has(id)) return { n: 0, top: id };
    visiting.add(id);
    let best = { n: 1, top: id };
    for (const p of parentsOf(g, id)) {
      if (!include(p)) continue;
      const u = up(p);
      if (u.n + 1 > best.n) best = { n: u.n + 1, top: u.top };
    }
    visiting.delete(id);
    memo.set(id, best);
    return best;
  };
  let best: { n: number; top: Id; bottom: Id } | undefined;
  for (const id of ids) {
    const u = up(id);
    if (!best || u.n > best.n) best = { ...u, bottom: id };
  }
  return best;
}

/** A birth date's month and day, when it's an exact Gregorian date that has them. */
function birthday(p: Person): { year: number; month: number; day?: number } | undefined {
  const raw = birthDate(p);
  const d = countableDate(raw);
  if (!d?.month || parseGedcomDate(raw)?.qualifier !== 'exact') return undefined;
  return { year: d.year, month: d.month, day: d.day };
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
/** "34 years", "1 year", or "67–68 years". */
const yearsText = (a: Omit<Age, 'atDeath'>) => `${ageText(a)} ${a.upTo === undefined && a.age === 1 ? 'year' : 'years'}`;
const average = (xs: number[]) => xs.reduce((s, x) => s + x, 0) / xs.length;
function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export function treeStats(tree: Tree, now = new Date()): TreeStats {
  const people = Object.values(tree.people);
  // Links to someone no longer in the tree (left by a partial import) aren't counted anywhere.
  const rels = Object.values(tree.relationships).filter((r) => tree.people[r.a] && tree.people[r.b]);
  const living = people.filter(isLiving);
  const deceased = people.filter(isDeceased);
  const g = graphOf(tree);

  // Births by decade, with empty decades filled in so the time axis is continuous.
  const years = people.map((p) => dateYear(birthDate(p))).filter((y): y is number => y !== undefined);
  const birthDecades: Count[] = [];
  if (years.length) {
    const lo = Math.floor(Math.min(...years) / 10) * 10;
    const hi = Math.floor(Math.max(...years) / 10) * 10;
    for (let d = lo; d <= hi; d += 10) birthDecades.push({ key: String(d), label: `${d}s`, count: years.filter((y) => y >= d && y < d + 10).length });
  }

  const birthMonths = MONTH_SHORT.map((label, i) => ({ key: String(i + 1), label, count: 0 }));
  for (const p of people) {
    const b = birthday(p);
    if (b) birthMonths[b.month - 1].count++;
  }

  const ages = people.map((p) => ({ p, a: ageOf(p, now) }));
  const livingAges = ageBands(ages.filter((x) => x.a && !x.a.atDeath && isLiving(x.p)).map((x) => x.a!.age));
  // A range of possible ages (a birth year alone) counts as its middle.
  const deathAges = ageBands(ages.filter((x) => x.a?.atDeath).map((x) => Math.floor(midAge(x.a!))));

  const terms: Record<TermGender, number> = { feminine: 0, masculine: 0, neutral: 0 };
  for (const p of people) terms[termGender(p)]++;

  const parentRels = rels.filter((r) => r.kind === 'parent');
  const partnerRels = rels.filter((r) => r.kind === 'partner');
  const socialRels = rels.filter((r) => r.kind === 'social');

  const pictured = new Set(Object.values(tree.media).filter((m) => m.kind === 'image').flatMap((m) => m.people.map((x) => x.personId)));
  const birthPlace = (p: Person) => p.events.find((e) => e.type === 'birth')?.place?.trim() ?? '';
  const completeness: Count[] = [
    { key: 'birth-date', label: 'Birth date', count: people.filter((p) => dateYear(birthDate(p)) !== undefined).length },
    { key: 'birthplace', label: 'Birthplace', count: people.filter((p) => birthPlace(p)).length },
    { key: 'parent', label: 'A parent', count: people.filter((p) => parentsOf(g, p.id).length > 0).length },
    { key: 'photo', label: 'A photo', count: people.filter((p) => p.photo || pictured.has(p.id)).length },
    { key: 'source', label: 'A source', count: people.filter((p) => p.citations?.length || p.events.some((e) => e.citations?.length)).length },
  ];

  return {
    people: people.length,
    living: living.length,
    deceased: deceased.length,
    notRecorded: people.length - living.length - deceased.length,
    generations: longestLine(g, Object.keys(tree.people))?.n ?? 0,
    partnerships: partnerRels.length,
    groups: Object.keys(tree.groups).length,
    genders: tally(
      people.map((p) => (p.gender.identity === 'custom' ? `custom:${genderLabel(p.gender)}` : p.gender.identity)),
      Infinity,
      (k) => (k.startsWith('custom:') ? k.slice(7) : GENDER_LABELS[k as GenderIdentity] ?? k),
    ),
    terms,
    pronouns: [
      ...tallyText(people.map((p) => p.pronouns ?? ''), 7),
      ...(() => {
        const none = people.filter((p) => !normalize(p.pronouns ?? '')).length;
        return none ? [{ key: '__none', label: 'Not recorded', count: none }] : [];
      })(),
    ],
    birthDecades,
    birthYears: years.length ? [Math.min(...years), Math.max(...years)] : undefined,
    birthMonths: birthMonths.some((m) => m.count) ? birthMonths : [],
    livingAges,
    deathAges,
    surnames: tallyText(people.map((p) => primaryName(p)?.surname ?? ''), 10),
    surnameCount: tallyText(people.map((p) => primaryName(p)?.surname ?? '')).length,
    birthplaces: tallyText(people.map(birthPlace), 10),
    // Each person counts once per occupation, however many times it's recorded.
    occupations: tallyText(people.flatMap((p) => [...new Map(p.events.filter((e) => e.type === 'occupation' && e.label?.trim()).map((e) => [normalize(e.label!), e.label!])).values()]), 10),
    completeness,
    deathDates: { known: deceased.filter((p) => dateYear(deathDate(p)) !== undefined).length, of: deceased.length },
    parentLinks: tally(parentRels.map((r) => r.type), Infinity, (k) => PARENT_LABELS[k as ParentType] ?? k),
    partnerKinds: tally(partnerRels.map((r) => r.type), Infinity, (k) => PARTNER_LABELS[k as PartnerType] ?? k),
    socialKinds: tally(socialRels.map((r) => r.type), Infinity, (k) => SOCIAL_LABELS[k as SocialType] ?? k),
    facts: factsOf(tree, g, now),
    checks: checksOf(tree, now),
  };
}

function factsOf(tree: Tree, g: Graph, now: Date): Fact[] {
  const people = Object.values(tree.people);
  const rels = Object.values(tree.relationships).filter((r) => tree.people[r.a] && tree.people[r.b]);
  const facts: Fact[] = [];
  const name = (id: Id) => displayName(tree.people[id]);
  const both = (a: Id, b: Id) => `${name(a)} and ${name(b)}`;

  // ---- Lives
  const withAge = people.map((p) => ({ p, a: ageOf(p, now) })).filter((x) => x.a);
  const oldestLiving = withAge.filter((x) => !x.a!.atDeath && isLiving(x.p) && x.a!.age <= OLDEST_PLAUSIBLE).sort((a, b) => midAge(b.a!) - midAge(a.a!))[0];
  if (oldestLiving) facts.push({ text: `The oldest living person is ${name(oldestLiving.p.id)}, at ${ageText(oldestLiving.a!)}.`, label: 'Oldest living person', value: ageText(oldestLiving.a!), note: `years old, born ${dateYear(birthDate(oldestLiving.p))}`, topic: 'life', people: [oldestLiving.p.id] });
  const lifespans = withAge.filter((x) => x.a!.atDeath).sort((a, b) => midAge(b.a!) - midAge(a.a!));
  if (lifespans.length) facts.push({ text: `The longest life recorded is ${name(lifespans[0].p.id)}'s: ${yearsText(lifespans[0].a!)}.`, label: 'Longest life', value: yearsText(lifespans[0].a!), note: lifeSpan(lifespans[0].p), topic: 'life', people: [lifespans[0].p.id] });
  const deathAges = lifespans.map((x) => midAge(x.a!));
  if (deathAges.length >= 3) facts.push({ text: `On average, people lived to ${Math.round(average(deathAges))}; half lived to ${Math.round(median(deathAges))} or more (from ${deathAges.length} recorded lifespans).`, label: 'Average lifespan', value: `${Math.round(average(deathAges))} years`, note: `Median ${Math.round(median(deathAges))}, from ${deathAges.length} recorded lifespans`, topic: 'life' });

  const born = people.map((p) => ({ p, y: dateYear(birthDate(p)) })).filter((x) => x.y !== undefined).sort((a, b) => a.y! - b.y!);
  if (born.length >= 2 && born.at(-1)!.y! > born[0].y!) {
    const first = born[0];
    const last = born.at(-1)!;
    const span = last.y! - first.y!;
    facts.push({ text: `Recorded births span ${span} ${plural(span, 'year', 'years')}, from ${name(first.p.id)} (${first.y}) to ${name(last.p.id)} (${last.y}).`, label: 'Births span', value: `${span} ${plural(span, 'year', 'years')}`, note: `${first.y} to ${last.y}, earliest and latest`, topic: 'life', people: [first.p.id, last.p.id] });
  }

  // Shared birthdays: exact dates with a month and day, in any year.
  const byDay = new Map<string, Id[]>();
  for (const p of people) {
    const b = birthday(p);
    if (b?.day) byDay.set(`${b.month}-${b.day}`, [...(byDay.get(`${b.month}-${b.day}`) ?? []), p.id]);
  }
  const shared = [...byDay].filter(([, ids]) => ids.length >= 2).sort((a, b) => b[1].length - a[1].length);
  if (shared.length) {
    const dayText = (k: string) => {
      const [m, d] = k.split('-').map(Number);
      return `${MONTH_LONG[m - 1]} ${d}`;
    };
    const [day, ids] = shared[0];
    const tied = shared.length > 1 && shared[1][1].length === ids.length;
    const tile = { label: 'Shared birthday', topic: 'birthday' as const };
    if (shared.length === 1) facts.push({ text: ids.length === 2 ? `${both(ids[0], ids[1])} share a birthday: ${dayText(day)}.` : `${ids.length} people share a birthday: ${dayText(day)}.`, ...tile, value: dayText(day), note: `${ids.length} people born on this day`, people: ids.slice(0, 5) });
    else if (tied) {
      const top = shared.filter(([, x]) => x.length === ids.length);
      const days = top.slice(0, 3).map(([d]) => dayText(d));
      facts.push({
        text: `${shared.length} birthdays are shared by two or more people, up to ${ids.length} on one day.`,
        ...tile,
        label: 'Shared birthdays',
        value: `${shared.length} days`,
        note: `${days.join(', ')}${top.length > 3 ? ` and ${top.length - 3} more` : ''}: ${ids.length} people each`,
        people: top.slice(0, 3).flatMap(([, x]) => x).slice(0, 6),
      });
    }
    else facts.push({ text: `${shared.length} birthdays are shared by two or more people. The most shared is ${dayText(day)}, with ${ids.length} people.`, ...tile, label: 'Most shared birthday', value: dayText(day), note: `${ids.length} people; ${shared.length} birthdays are shared in all`, people: ids.slice(0, 5) });
  }

  // ---- Family
  const livingIds = new Set(people.filter(isLiving).map((p) => p.id));
  const livingLine = longestLine(g, livingIds, (id) => livingIds.has(id));
  if (livingLine && livingLine.n >= 3) facts.push({ text: `${livingLine.n} generations are living, from ${name(livingLine.top)} to ${name(livingLine.bottom)}.`, label: 'Living generations', value: String(livingLine.n), note: 'alive at once, eldest to youngest', topic: 'family', people: [livingLine.top, livingLine.bottom] });

  const kids = people.map((p) => ({ id: p.id, n: new Set((g.childRels.get(p.id) ?? []).map((r) => r.b)).size })).sort((a, b) => b.n - a.n);
  if (kids[0]?.n >= 2) {
    const ties = kids.filter((k) => k.n === kids[0].n);
    facts.push({
      text: ties.length === 1 ? `${name(ties[0].id)} has the most children recorded: ${ties[0].n}.` : ties.length === 2 ? `${both(ties[0].id, ties[1].id)} have the most children recorded: ${kids[0].n} each.` : `${ties.length} people share the most children recorded (${kids[0].n} each).`,
      label: 'Most children',
      value: String(kids[0].n),
      note: ties.length > 2 ? `children each, for ${ties.length} people` : ties.length === 2 ? 'children each' : 'children recorded',
      topic: 'family',
      people: ties.slice(0, 5).map((t) => t.id),
    });
  }
  const parents = kids.filter((k) => k.n > 0);
  if (parents.length >= 3) facts.push({ text: `Parents have ${average(parents.map((k) => k.n)).toFixed(1)} children recorded, on average.`, label: 'Children per parent', value: average(parents.map((k) => k.n)).toFixed(1), note: `on average, across ${parents.length} parents`, topic: 'family' });

  // Most descendants: someone with a parent has fewer than that parent, so only look at people without one.
  let most = { n: 0, ids: [] as Id[], depths: [] as number[] };
  for (const p of people) {
    if (parentsOf(g, p.id).length) continue;
    const d = descendantsOf(g, p.id);
    if (d.size > most.n) most = { n: d.size, ids: [p.id], depths: [Math.max(...d.values())] };
    else if (d.size && d.size === most.n) most = { ...most, ids: [...most.ids, p.id], depths: [...most.depths, Math.max(...d.values())] };
  }
  if (most.n >= 3 && Math.max(...most.depths) >= 2) {
    const over = most.depths.every((d) => d === most.depths[0]) ? `, over ${most.depths[0]} generations` : '';
    const [a, b] = most.ids;
    facts.push({
      text: most.ids.length === 1 ? `${name(a)} has the most descendants recorded: ${most.n}${over}.` : most.ids.length === 2 ? `${both(a, b)} have the most descendants recorded: ${most.n}${over}.` : `${most.ids.length} people share the most descendants recorded (${most.n} each).`,
      label: 'Most descendants',
      value: String(most.n),
      note: most.ids.length > 2 ? `each, for ${most.ids.length} people` : over ? `over ${most.depths[0]} generations` : 'recorded',
      topic: 'family',
      people: most.ids.slice(0, 5),
    });
  }

  // Twins and other multiples: children of the same biological parent born on the same day.
  const sets = new Map<string, Id[]>();
  for (const p of people) {
    const sameDay = new Map<string, Set<Id>>();
    for (const r of g.childRels.get(p.id) ?? []) {
      if (r.type !== 'biological') continue;
      const b = birthday(tree.people[r.b]);
      if (!b?.day) continue;
      const k = `${b.year}-${b.month}-${b.day}`;
      sameDay.set(k, (sameDay.get(k) ?? new Set()).add(r.b));
    }
    // Two children with the same name and birthday are one child entered twice, not twins.
    for (const ids of sameDay.values()) {
      const distinct = [...new Map([...ids].map((id) => [normalize(displayName(tree.people[id])), id])).values()];
      if (distinct.length >= 2) sets.set(distinct.sort().join(' '), distinct);
    }
  }
  if (sets.size) {
    const all = [...sets.values()];
    const multiples = all.some((s) => s.length > 2) ? 'twins or other multiples' : 'twins';
    facts.push({ text: all.length === 1 ? `One set of ${multiples} is recorded: ${all[0].map(name).join(' and ')}.` : `${all.length} sets of ${multiples} are recorded.`, label: multiples === 'twins' ? 'Twins' : 'Twins and multiples', value: String(all.length), note: `${plural(all.length, 'set', 'sets')}: siblings born on the same day`, topic: 'family', people: all.flat().slice(0, 6) });
  }

  // Age of biological parents when each child was born.
  const parentAges = rels.flatMap((r) => {
    if (r.kind !== 'parent' || r.type !== 'biological') return [];
    const span = yearsBetween(birthDate(tree.people[r.a]), birthDate(tree.people[r.b]));
    return span ? [midAge(span)] : [];
  });
  if (parentAges.length >= 3) facts.push({ text: `Biological parents were ${Math.round(average(parentAges))} on average when a child was born (from ${parentAges.length} parent–child pairs with both birth dates).`, label: "Parents' age at a birth", value: String(Math.round(average(parentAges))), note: `average for biological parents, from ${parentAges.length} births`, topic: 'family' });

  // ---- Partners
  const firstMarriage = new Map<Id, string>();
  for (const r of rels) {
    if (r.kind !== 'partner' || r.type !== 'married' || !countableDate(r.start)) continue;
    for (const id of [r.a, r.b]) {
      const prev = firstMarriage.get(id);
      if (!prev || dateSortKey(r.start) < dateSortKey(prev)) firstMarriage.set(id, r.start!);
    }
  }
  const marriageAges = [...firstMarriage].flatMap(([id, start]) => {
    const span = yearsBetween(birthDate(tree.people[id]), start);
    return span ? [midAge(span)] : [];
  });
  if (marriageAges.length >= 3) facts.push({ text: `People first married at ${Math.round(average(marriageAges))} on average (from ${marriageAges.length} people with a birth date and a wedding date).`, label: 'Age at first marriage', value: String(Math.round(average(marriageAges))), note: `average, from ${marriageAges.length} people`, topic: 'partners' });

  let longest: { a: Id; b: Id; span: Omit<Age, 'atDeath'>; ongoing: boolean; married: boolean } | undefined;
  for (const r of rels) {
    if (r.kind !== 'partner' || !countableDate(r.start)) continue;
    const pair = [tree.people[r.a], tree.people[r.b]];
    let end: string | Date | undefined;
    let ongoing = false;
    if (countableDate(r.end)) end = r.end;
    else if (r.status === 'current' || r.status === 'widowed') {
      // Without an end date, a partnership lasts until today, or until the first partner died.
      const dead = pair.filter(isDeceased);
      const deaths = dead.map(deathDate).filter((d) => countableDate(d)).sort((x, y) => dateSortKey(x) - dateSortKey(y));
      if (!dead.length && r.status === 'current') [end, ongoing] = [now, true];
      else if (dead.length && deaths.length === dead.length) end = deaths[0];
    }
    const span = yearsBetween(r.start, end);
    if (!span || span.age < 1) continue;
    // "Still married after 70 years" only when both are plausibly alive: recorded as living, or
    // under 90, or of unknown age in a partnership young enough. Otherwise a death went unrecorded.
    const alive = (p: Person) => isLiving(p) || ((a) => (a ? a.age < 90 : span.age < 60))(ageOf(p, now));
    if (ongoing && !pair.every(alive)) continue;
    if (!longest || midAge(span) > midAge(longest.span)) longest = { a: r.a, b: r.b, span, ongoing, married: r.type === 'married' };
  }
  if (longest) {
    const how = longest.married ? 'married' : 'together';
    facts.push({
      text: `${both(longest.a, longest.b)} ${longest.ongoing ? 'have been' : 'were'} ${how} for ${yearsText(longest.span)}, the longest partnership recorded.`,
      label: 'Longest partnership',
      value: yearsText(longest.span),
      note: longest.ongoing ? `${how}, and counting` : how,
      topic: 'partners',
      people: [longest.a, longest.b],
    });
  }

  // ---- Identity
  const trans = people.filter((p) => ['trans-woman', 'trans-man', 'transfeminine', 'transmasculine'].includes(p.gender.identity) || p.events.some((e) => e.type === 'hrt-start' || e.type === 'gender-transition'));
  if (trans.length) facts.push({ text: `${trans.length} ${plural(trans.length, 'person is', 'people are')} trans or recorded a transition.`, label: 'Trans', value: String(trans.length), note: `${plural(trans.length, 'person is', 'people are')} trans or recorded a transition`, topic: 'identity' });
  const beyondBinary = people.filter((p) => !['woman', 'man', 'trans-woman', 'trans-man', 'unknown'].includes(p.gender.identity));
  if (beyondBinary.length) facts.push({ text: `${beyondBinary.length} ${plural(beyondBinary.length, 'person identifies', 'people identify')} outside the gender binary.`, label: 'Beyond the binary', value: String(beyondBinary.length), note: `${plural(beyondBinary.length, 'person identifies', 'people identify')} outside the gender binary`, topic: 'identity' });

  // ---- Names, groups, and how it all connects
  const first = tallyText(people.map((p) => primaryName(p)?.given.trim().split(/\s+/)[0] ?? ''))[0];
  if (first && first.count >= 2) facts.push({ text: `The most common first name is ${first.label}, shared by ${first.count} people.`, label: 'Most common first name', value: first.label, note: `shared by ${first.count} people`, topic: 'names' });

  const groups = Object.values(tree.groups)
    .map((grp) => ({ grp, n: new Set(grp.members.map((m) => m.personId).filter((id) => tree.people[id])).size }))
    .sort((a, b) => b.n - a.n);
  if (groups[0]?.n) facts.push({ text: `The biggest group is ${groups[0].grp.name}, with ${groups[0].n} ${plural(groups[0].n, 'member', 'members')}.`, label: 'Biggest group', value: groups[0].grp.name, note: `${groups[0].n} ${plural(groups[0].n, 'member', 'members')}`, topic: 'groups' });

  const sizes = componentSizes(g);
  const alone = sizes.filter((s) => s === 1).length;
  const linked = sizes.length - alone;
  const loners = `${alone} ${plural(alone, 'person has', 'people have')} no relationships recorded`;
  if (linked >= 2) facts.push({ text: `The tree has ${linked} separate families that aren't linked to each other (the biggest has ${sizes[0]} people)${alone ? `, and ${loners}` : ''}.`, label: 'Separate families', value: String(linked), note: `not linked to each other; the biggest has ${sizes[0]} people`, topic: 'links' });
  else if (alone && people.length > 1) facts.push({ text: `${loners}, so they aren't linked to anyone else in the tree.`, label: 'Not linked', value: String(alone), note: `${plural(alone, 'person has', 'people have')} no relationships recorded`, topic: 'links' });

  return facts;
}

/** One record that contradicts another, about one person: the kind of problem and a sentence saying exactly what. */
export interface DateProblem {
  kind: 'death-before-birth' | 'too-old' | 'before-parent' | 'after-parent-died' | 'partner-before-birth';
  personId: Id;
  /** The other person involved, if any (the parent, the partner). */
  otherId?: Id;
  detail: string;
}

/** Records that contradict each other or can't be right, so they're probably typos. */
export function dateProblems(tree: Tree, now = new Date()): DateProblem[] {
  const people = Object.values(tree.people);
  const rels = Object.values(tree.relationships).filter((r) => tree.people[r.a] && tree.people[r.b]);
  const out: DateProblem[] = [];
  const name = (id: Id) => displayName(tree.people[id]);
  const by = (p: Person) => dateYear(birthDate(p));
  const dy = (p: Person) => dateYear(deathDate(p));

  for (const p of people) {
    if (by(p) !== undefined && dy(p) !== undefined && dy(p)! < by(p)!)
      out.push({ kind: 'death-before-birth', personId: p.id, detail: `${name(p.id)} is recorded as dying in ${dy(p)}, before being born in ${by(p)}.` });
    const age = isLiving(p) ? ageOf(p, now) : undefined;
    if (age && age.age > OLDEST_PLAUSIBLE)
      out.push({ kind: 'too-old', personId: p.id, detail: `${name(p.id)} is recorded as living but was born in ${by(p)}, so would be ${ageText(age)}.` });
  }
  for (const r of rels) {
    if (r.kind === 'parent' && r.type === 'biological') {
      const [parent, child] = [tree.people[r.a], tree.people[r.b]];
      if (by(parent) !== undefined && by(child) !== undefined && by(parent)! > by(child)!)
        out.push({ kind: 'before-parent', personId: r.b, otherId: r.a, detail: `${name(r.b)} is recorded as born in ${by(child)}, before ${name(r.a)}, a biological parent, in ${by(parent)}.` });
      // A child can be born months after a parent dies, but not years.
      if (dy(parent) !== undefined && by(child) !== undefined && by(child)! > dy(parent)! + 1)
        out.push({ kind: 'after-parent-died', personId: r.b, otherId: r.a, detail: `${name(r.b)} is recorded as born in ${by(child)}, ${by(child)! - dy(parent)!} years after ${name(r.a)}, a biological parent, died in ${dy(parent)}.` });
    }
    if (r.kind === 'partner') {
      const y = dateYear(r.start);
      if (y === undefined) continue;
      for (const [id, other] of [[r.a, r.b], [r.b, r.a]])
        if ((by(tree.people[id]) ?? -Infinity) > y)
          out.push({ kind: 'partner-before-birth', personId: id, otherId: other, detail: `${name(id)}'s partnership with ${name(other)} is recorded as starting in ${y}, before ${name(id)} was born in ${by(tree.people[id])}.` });
    }
  }
  return out;
}

const CHECK_WORDS: Record<DateProblem['kind'], [string, string]> = {
  'death-before-birth': ['person died before they were born', 'people died before they were born'],
  'too-old': [`person is recorded as living but would be over ${OLDEST_PLAUSIBLE}`, `people are recorded as living but would be over ${OLDEST_PLAUSIBLE}`],
  'before-parent': ['person was born before their biological parent', 'people were born before their biological parent'],
  'after-parent-died': ['person was born more than a year after their biological parent died', 'people were born more than a year after their biological parent died'],
  'partner-before-birth': ['person has a partnership that starts before they were born', 'people have a partnership that starts before they were born'],
};

/** The date problems, counted by kind for Reports. */
function checksOf(tree: Tree, now: Date): Fact[] {
  const problems = dateProblems(tree, now);
  return (Object.keys(CHECK_WORDS) as DateProblem['kind'][]).flatMap((kind) => {
    const ids = [...new Set(problems.filter((x) => x.kind === kind).map((x) => x.personId))];
    if (!ids.length) return [];
    const [one, many] = CHECK_WORDS[kind];
    return [{ text: `${ids.length} ${plural(ids.length, one, many)}.`, people: ids.slice(0, 5) }];
  });
}
